/**
 * useConversations
 * Fetches and polls the conversation list every 60 seconds.
 *
 * Accepts an optional `service` parameter for dependency injection.
 * Default: chatService (regular user). Admin can pass adminChatService.
 *
 * Bug-avoidance notes:
 *  - loading initialized as false (not true) — avoids permanent spinner
 *    when conversationId is absent or the component unmounts immediately.
 *  - fetchConversations has empty deps [] — no state in the dependency
 *    array, so the callback is stable across renders.
 */

import { useState, useEffect, useCallback } from 'react';
import { chatService as defaultService } from '../services/chatService';

const POLL_INTERVAL_MS = 60_000;

/**
 * @param {object} [service] - Service with getConversations(). Defaults to chatService.
 * @returns {{
 *   conversations: ConversationSummary[],
 *   loading: boolean,
 *   error: string|null,
 *   refresh: () => Promise<void>
 * }}
 */
export default function useConversations(service = defaultService) {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false); // false — not true
  const [error, setError] = useState(null);

  const fetchConversations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await service.getConversations();
      setConversations(data);
    } catch (err) {
      setError(err.message || 'Gesprekken konden niet worden geladen');
    } finally {
      setLoading(false);
    }
  }, [service]); // service in deps — stable reference expected

  useEffect(() => {
    fetchConversations();
    const intervalId = setInterval(fetchConversations, POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [fetchConversations]);

  return { conversations, loading, error, refresh: fetchConversations };
}
